/**
 * Gráficos do PDF "Por que consórcio" em SVG puro, como texto.
 *
 * O PDF é HTML fotografado pelo html2canvas; SVG inline sai nítido e não pede
 * biblioteca de gráfico nenhuma (o recharts precisa de React montado e de
 * medir o container, o que não existe num HTML montado fora da tela).
 *
 * Fonte do texto do SVG: Helvetica/Arial — o SVG vira imagem na captura e
 * imagem não enxerga fonte web.
 */

export type SerieLinha = {
  nome: string;
  cor: string;
  valores: number[];
  tracejado?: boolean;
  /** Pinta a área abaixo da linha com a cor em degradê. */
  area?: boolean;
  /** Espessura da linha (padrão 2,5). */
  espessura?: number;
};

const FONTE = "Helvetica, Arial, sans-serif";

/** "R$ 1,2 mi", "R$ 468 mil", "R$ 950". Para eixo e rótulo de ponta, onde centavo é ruído. */
export const reaisCurto = (v: number): string => {
  const a = Math.abs(v);
  const sinal = v < 0 ? '−' : '';
  if (a >= 1e6) return `${sinal}R$ ${(a / 1e6).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi`;
  if (a >= 1e3) return `${sinal}R$ ${(a / 1e3).toLocaleString('pt-BR', { maximumFractionDigits: a >= 1e5 ? 0 : 1 })} mil`;
  return `${sinal}R$ ${Math.round(a).toLocaleString('pt-BR')}`;
};

/** Limites "redondos" do eixo, com 4 divisões. */
export const escalaY = (min: number, max: number): { min: number; max: number; passo: number } => {
  const lo = Math.min(0, min);
  const hi = max > lo ? max : lo + 1;
  const bruto = (hi - lo) / 4;
  const mag = Math.pow(10, Math.floor(Math.log10(bruto)));
  const passo = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((p) => p >= bruto) ?? bruto;
  return { min: Math.floor(lo / passo) * passo, max: Math.ceil(hi / passo) * passo, passo };
};

const escapar = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);

let contador = 0;

/** Valor cheio para a dica do gráfico (no eixo, o curto basta). */
const reaisCheio = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** JSON num atributo HTML: a tela lê no passar do mouse (o PDF ignora). */
const atributoDica = (o: unknown) => ` data-dica="${escapar(JSON.stringify(o))}"`;

export type Marcador = {
  indice: number;
  rotulo: string;
  cor?: string;
  corTexto?: string;
  /** Linha do rótulo (0, 1…), para dois marcadores próximos não se cobrirem. */
  linha?: number;
};

export const graficoLinhas = (o: {
  largura: number;
  altura: number;
  series: SerieLinha[];
  /** Rótulo do ponto i (0-based) no eixo X. */
  rotuloX: (i: number) => string;
  /** Linha vertical de destaque (índice do ponto) com rótulo. */
  marcador?: Marcador;
  /** Mais linhas verticais de destaque (ex.: o plano e o empate). */
  marcadores?: Marcador[];
  /** Pontos soltos (ex.: outros planos), com rótulo curto ao lado. */
  pontos?: Array<{ indice: number; valor: number; cor: string; rotulo?: string; rotuloAbaixo?: boolean }>;
  /** Mostra o valor final de cada série na ponta da linha. */
  valorNaPonta?: boolean;
  /** Formato do eixo Y e dos valores na ponta (padrão: reais curtos). */
  formatoY?: (v: number) => string;
}): string => {
  const fmt = o.formatoY ?? reaisCurto;
  const id = `g${++contador}`;
  const mEsq = 64;
  const mDir = o.valorNaPonta ? 86 : 16;
  const mTop = 14;
  const mBase = 26;
  const w = o.largura - mEsq - mDir;
  const h = o.altura - mTop - mBase;
  const n = Math.max(...o.series.map((s) => s.valores.length), 2);
  const todos = o.series.flatMap((s) => s.valores).filter(Number.isFinite);
  const esc = escalaY(Math.min(...todos, 0), Math.max(...todos, 1));
  const x = (i: number) => mEsq + (i / (n - 1)) * w;
  const y = (v: number) => mTop + h - ((v - esc.min) / (esc.max - esc.min)) * h;

  const grade: string[] = [];
  for (let v = esc.min; v <= esc.max + esc.passo / 2; v += esc.passo) {
    grade.push(
      `<line x1="${mEsq}" x2="${mEsq + w}" y1="${y(v)}" y2="${y(v)}" stroke="${v === 0 ? '#9aa6b5' : '#e3e8ef'}" stroke-width="1"/>` +
        `<text x="${mEsq - 8}" y="${y(v) + 4}" text-anchor="end" font-size="10" fill="#7a8696" font-family="${FONTE}">${fmt(v)}</text>`,
    );
  }
  const marcasX = [0, Math.round((n - 1) / 3), Math.round((2 * (n - 1)) / 3), n - 1]
    .filter((v, i, a) => a.indexOf(v) === i)
    .map(
      (i) =>
        `<text x="${x(i)}" y="${o.altura - 6}" text-anchor="${i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}" font-size="10" fill="#7a8696" font-family="${FONTE}">${escapar(o.rotuloX(i))}</text>`,
    );

  const defs: string[] = [];
  const corpo: string[] = [];
  o.series.forEach((s, k) => {
    const pts = s.valores.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`);
    if (pts.length === 0) return;
    if (s.area) {
      defs.push(
        `<linearGradient id="${id}a${k}" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${s.cor}" stop-opacity="0.28"/><stop offset="1" stop-color="${s.cor}" stop-opacity="0.02"/></linearGradient>`,
      );
      corpo.push(
        `<polygon points="${x(0)},${y(Math.max(esc.min, 0))} ${pts.join(' ')} ${x(s.valores.length - 1)},${y(Math.max(esc.min, 0))}" fill="url(#${id}a${k})"/>`,
      );
    }
    corpo.push(
      `<polyline points="${pts.join(' ')}" fill="none" stroke="${s.cor}" stroke-width="${s.espessura ?? 2.5}" stroke-linejoin="round" stroke-linecap="round"${s.tracejado ? ' stroke-dasharray="6 5"' : ''}/>`,
    );
  });

  // Valores na ponta, afastados para não se sobreporem.
  const pontas: string[] = [];
  if (o.valorNaPonta) {
    const itens = o.series
      .filter((s) => s.valores.length > 0)
      .map((s) => ({ s, yv: y(s.valores[s.valores.length - 1]), xv: x(s.valores.length - 1) }))
      .sort((a, b) => a.yv - b.yv);
    let ultimo = -Infinity;
    for (const it of itens) {
      const yr = Math.max(it.yv, ultimo + 14);
      ultimo = yr;
      pontas.push(
        `<circle cx="${it.xv}" cy="${it.yv}" r="4" fill="#fff" stroke="${it.s.cor}" stroke-width="2.5"/>` +
          `<text x="${it.xv + 9}" y="${yr + 4}" font-size="11" font-weight="700" fill="${it.s.cor}" font-family="${FONTE}">${fmt(it.s.valores[it.s.valores.length - 1])}</text>`,
      );
    }
  }

  const marcador = [...(o.marcador ? [o.marcador] : []), ...(o.marcadores ?? [])]
    .map((m) => {
      const cor = m.cor ?? '#C4AD8E';
      const larg = Math.max(60, m.rotulo.length * 6.2 + 16);
      // O rótulo não sai do gráfico nas pontas.
      const cx = Math.min(Math.max(x(m.indice), mEsq + larg / 2), mEsq + w - larg / 2);
      const topo = mTop - 2 + (m.linha ?? 0) * 22;
      return (
        `<line x1="${x(m.indice)}" x2="${x(m.indice)}" y1="${mTop}" y2="${mTop + h}" stroke="${cor}" stroke-width="1.5" stroke-dasharray="4 4"/>` +
        `<rect x="${cx - larg / 2}" y="${topo}" width="${larg}" height="18" rx="9" fill="${cor}"/>` +
        `<text x="${cx}" y="${topo + 13}" text-anchor="middle" font-size="10" font-weight="700" fill="${m.corTexto ?? '#1f2c40'}" font-family="${FONTE}">${escapar(m.rotulo)}</text>`
      );
    })
    .join('');

  const soltos = (o.pontos ?? [])
    .filter((p) => p.indice >= 0 && p.indice < n && Number.isFinite(p.valor))
    .map(
      (p) =>
        `<circle cx="${x(p.indice)}" cy="${y(p.valor)}" r="5" fill="${p.cor}" stroke="#fff" stroke-width="2"/>` +
        (p.rotulo
          ? `<text x="${x(p.indice) + 8}" y="${y(p.valor) + (p.rotuloAbaixo ? 16 : -7)}" font-size="9.5" font-weight="700" fill="${p.cor}" font-family="${FONTE}">${escapar(p.rotulo)}</text>`
          : ''),
    )
    .join('');

  const fmtDica = o.formatoY ?? reaisCheio;
  const dica = atributoDica({
    tipo: 'linhas',
    x0: mEsq,
    w,
    topo: mTop,
    h,
    rotulos: Array.from({ length: n }, (_, i) => o.rotuloX(i)),
    series: o.series.map((s) => ({
      nome: s.nome,
      cor: s.cor,
      y: s.valores.map((v) => Math.round(y(v) * 10) / 10),
      v: s.valores.map((v) => fmtDica(v)),
    })),
  });
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${o.largura}" height="${o.altura}" viewBox="0 0 ${o.largura} ${o.altura}"${dica}><defs>${defs.join('')}</defs>${grade.join('')}${marcasX.join('')}${corpo.join('')}${marcador}${soltos}${pontas.join('')}</svg>`;
};

export type GrupoBarras = { rotulo: string; valores: Array<{ valor: number; cor: string; dica?: string }> };

export const graficoBarras = (o: {
  largura: number;
  altura: number;
  grupos: GrupoBarras[];
  /** Mostra o valor em cima de cada barra. */
  valorNoTopo?: boolean;
}): string => {
  const mEsq = 64;
  const mTop = 20;
  const mBase = 26;
  const w = o.largura - mEsq - 10;
  const h = o.altura - mTop - mBase;
  const todos = o.grupos.flatMap((g) => g.valores.map((v) => v.valor));
  const esc = escalaY(0, Math.max(...todos, 1));
  const y = (v: number) => mTop + h - ((v - esc.min) / (esc.max - esc.min)) * h;
  const largGrupo = w / Math.max(1, o.grupos.length);
  const nb = Math.max(1, ...o.grupos.map((g) => g.valores.length));
  const largBarra = Math.min(34, (largGrupo * 0.72) / nb);

  const grade: string[] = [];
  for (let v = esc.min; v <= esc.max + esc.passo / 2; v += esc.passo) {
    grade.push(
      `<line x1="${mEsq}" x2="${mEsq + w}" y1="${y(v)}" y2="${y(v)}" stroke="#e3e8ef"/>` +
        `<text x="${mEsq - 8}" y="${y(v) + 4}" text-anchor="end" font-size="10" fill="#7a8696" font-family="${FONTE}">${reaisCurto(v)}</text>`,
    );
  }
  const barras = o.grupos.flatMap((g, gi) => {
    const x0 = mEsq + gi * largGrupo + (largGrupo - largBarra * g.valores.length) / 2;
    const itens = g.valores.map((v, vi) => {
      const xb = x0 + vi * largBarra;
      const yb = y(v.valor);
      const topo = o.valorNoTopo
        ? `<text x="${xb + largBarra / 2}" y="${yb - 5}" text-anchor="middle" font-size="9" font-weight="700" fill="#1f2c40" font-family="${FONTE}">${reaisCurto(v.valor)}</text>`
        : '';
      const dica = v.dica ?? `${g.rotulo} · ${reaisCheio(v.valor)}`;
      return `<rect x="${xb + 1.5}" y="${yb}" width="${largBarra - 3}" height="${Math.max(0, y(0) - yb)}" rx="3" fill="${v.cor}" data-barra="${escapar(dica)}"/>${topo}`;
    });
    itens.push(
      `<text x="${mEsq + gi * largGrupo + largGrupo / 2}" y="${o.altura - 6}" text-anchor="middle" font-size="10" fill="#7a8696" font-family="${FONTE}">${escapar(g.rotulo)}</text>`,
    );
    return itens;
  });
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${o.largura}" height="${o.altura}" viewBox="0 0 ${o.largura} ${o.altura}">${grade.join('')}${barras.join('')}</svg>`;
};
